// Full column set for the IM Item Master grid — every frmIMM0049.qryIMItem field the GetItemMasterMain
// projection returns, with the Delphi captions. The 6 screenshot columns are visible; the rest are hidden
// (column chooser). dataFields are camelCase (OM controller serialization); rowid is the store key.
import { ITEM_STATUSES } from "../transactionsTab/codeLists";
import { ItemSourceFlag, ItemTypeFlag } from "../../../const/Lookup";

const money = { type: "fixedPoint", precision: 2 };
const txt = (dataField, caption, visible = false) => ({ dataField, caption, visible });
const num = (dataField, caption, visible = false) => ({ dataField, caption, visible, dataType: "number", format: money });
const dt = (dataField, caption, visible = false) => ({ dataField, caption, visible, dataType: "date", format: "MM/dd/yyyy" });
const dtm = (dataField, caption, visible = false) => ({ dataField, caption, visible, dataType: "datetime", format: "MM/dd/yyyy hh:mm a" });
const udf = (prefix, labelPrefix, kind) =>
  Array.from({ length: 10 }, (_, i) => {
    const n = i + 1;
    const field = `${prefix}${n}`;
    const label = `${labelPrefix} ${n}`;
    return kind === "date" ? dt(field, label) : kind === "num" ? num(field, label) : txt(field, label);
  });

const ITEM_MASTER_COLUMNS = [
  // Visible (matches the Delphi saved layout in the screenshot).
  txt("warehouseID", "Warehouse", true),
  txt("itemCode", "Item", true),
  txt("description_1", "Description - 1", true),
  txt("description_2", "Description - 2", true),
  num("onHand", "On Hand", true),
  num("netAvailable", "Net Available", true),

  // Hidden — full field set.
  txt("classCode", "Class"),
  txt("subClassCode", "Sub Class"),
  { dataField: "status", caption: "Status", visible: false, lookup: { dataSource: ITEM_STATUSES, valueExpr: "code", displayExpr: "label" } },
  { dataField: "itemType", caption: "Item Type", visible: false, lookup: { dataSource: ItemTypeFlag, valueExpr: "key", displayExpr: "value" } },
  txt("abcCode", "ABC Code"),
  txt("alternateSku", "Alternate SKU"),
  dt("anniversaryBillingDate", "Anniver Bill Date"),
  num("anniversaryBillingQuantity", "Anniver Bill Quant"),
  txt("anniversaryBillingFlag", "Anniversary Billing Flag"),
  txt("artermsdiscountflag", "AR Terms Disc Flag"),
  txt("artradediscountflag", "AR Trade Disc Flag"),
  num("areaAvailable", "Area Available"),
  txt("assemblyCode", "Assembly Code"),
  num("available", "Available"),
  num("averageCost", "Average Cost"),
  num("inBackOrder", "Back Order - In"),
  num("outBackOrder", "Back Order - Out"),
  txt("bomExplosionFlag", "BOM Exp Flag"),
  txt("boxCode", "Box Code"),
  txt("careInstructions", "Care Instruct"),
  txt("costBasis", "Cost Basis"),
  num("corePrice", "Core Price"),
  txt("creationUserId", "Created By"),
  dtm("creationDateTime", "Creation Date"),
  dt("availableDate", "Date Avail"),
  dt("dateExpiration", "Date Exp"),
  dt("dateIntroduced", "Date Intro"),
  num("emCostBucketNumber", "EM Cost Bucket No"),
  txt("freightClassCode", "Freight Class Code"),
  num("grossWeightAvailable", "Gross Wt Available"),
  num("grossWeightQuantity", "Gross Weight Qty"),
  txt("grossWeightUom", "Gross Weight UOM"),
  txt("hazardousFlag", "Hazard Flag"),
  num("inFuture", "In Future"),
  num("inOrdered", "In Ordered"),
  num("inReceiving", "In Receiving"),
  txt("inventoryUnitUom", "UOM"),
  txt("itemSupcCode", "Items UPC Code"),
  { dataField: "itemSourceFlag", caption: "Item Source Flag", visible: false, lookup: { dataSource: ItemSourceFlag, valueExpr: "key", displayExpr: "value" } },
  num("lastCost", "Last Cost"),
  dtm("lastUpdateDateTime", "Last Updated"),
  txt("lastUpdateUserId", "Last Updated By"),
  txt("locationType", "Location Type"),
  num("locationmaxunitsforwardpick", "Max Units Fwd Pick"),
  num("locationmaxunitsreplenishment", "Max Units Repl"),
  num("maximumPriceLevelForDiscount", "Max Price Level for Disc"),
  num("minimumsalesquantity", "Min Sales Qty"),
  num("locationminunitsforwardpick", "Min Units Fwd Pick"),
  num("locationminunitsreplenishment", "Min Units Repl"),
  txt("miscelleanousFeeFlag", "Misc Fee Flag"),
  num("netWeightAvailable", "Net Wt Available"),
  num("netWeightQuantity", "Net Weight Qty"),
  txt("netWeightUom", "Net Weight UOM"),
  txt("oemManufacturerCode", "OEM Code"),
  num("outFuture", "Out Future"),
  num("outOrdered", "Out Ordered"),
  num("packagesPerShippingUnitQty", "Packages per Ship Unit Quant"),
  txt("packagesPerShippingUnitUom", "Packages per Ship Unit UOM"),
  num("palletPatternColumn", "Pallet Pattern Column"),
  num("palletPatternHeight", "Pallet Pattern Height"),
  num("palletPatternRow", "Pallet Pattern Row"),
  txt("palletSizeCode", "Pallet Size Code"),
  txt("partCode", "Part Code"),
  txt("physicalCountCode", "Physical Count Code"),
  num("piecesPerUnitQuantity", "Pieces per Unit Quant"),
  txt("piecesPerUnitUom", "Pieces per Unit UOM"),
  num("priceLevelA", "Price Level A"),
  num("priceLevelB", "Price Level B"),
  num("priceLevelC", "Price Level C"),
  num("priceLevelD", "Price Level D"),
  num("priceLevelE", "Price Level E"),
  txt("statusReasonCode", "Reason Code"),
  txt("receiptUomAlternative", "Rec UOM Alt"),
  txt("receiptUomDefault", "Rec UOM Default"),
  txt("receiptUomMultiple", "Rec UOM Multi"),
  num("receiptPackSize", "Rec Pack Size"),
  num("reorderMaxDay", "ReOrder Max Days OnHand"),
  num("reorderMaxQty", "ReOrder Inv Max Qty"),
  num("reorderMinDay", "ReOrder Min Days OnHand"),
  num("reorderMinQty", "ReOrder Inv Min Qty"),
  num("reorderCalQty", "ReOrder Quant EOQ Calc"),
  num("reorderQuantityEntered", "ReOrder Quant Entered"),
  num("reorderQuantityOrderMultiple", "ReOrder Quant Order Multi"),
  num("reorderLeadTime", "ReOrder Lead Time"),
  txt("reorderMethodFlag", "ReOrder Method Flag"),
  num("reorderSafetyQuantity", "ReOrder Safety Quant"),
  num("saleIncrementQuantity", "Sales Inc"),
  num("salemaxquantity", "Sale Max Qty"),
  txt("shipSelection", "Ship Select"),
  txt("shipmentUomAlternative", "Ship UOM Alt"),
  txt("shipmentUomDefault", "Ship UOM Default"),
  txt("shipmentUomMultiple", "Ship UOM Multi"),
  num("shipmentPackSize", "Ship Pack Size"),
  txt("specificationCode", "Spec Code"),
  num("stackheight", "Stack Height"),
  num("standardcost", "Standard Cost"),
  dt("storageBillingEndDate", "Storage Bill End Date"),
  dt("storageBillingStartDate", "Storage Bill Start Date"),
  txt("storageLocationPriorityFlag", "Storage Loc Prior Flag"),
  txt("storageLocationType", "Storage Loc Type"),
  txt("systemCode", "System Code"),
  txt("taxstatus", "Tax Status"),
  txt("tlFuelTaxCode", "TL Fuel Tax Code"),
  txt("tlFuelTaxFlag", "TL Fuel Tax Flag"),
  num("unavailable", "Unavailable"),
  num("unitAreaQuantity", "Unit Area Quant"),
  txt("unitAreaUom", "Unit Area UOM"),
  num("unitValue", "Unit Value"),
  txt("unitValueuom", "Unit Value UOM"),
  num("unitVolumeQuantity", "Unit Vol Quant"),
  txt("unitVolumeUom", "Unit Volume UOM"),
  num("unitPckg", "Units/Pkg"),
  txt("unitPkguom", "Units/Pkg UOM"),
  num("unitsPerPackageQuantity", "Units per Package Quant"),
  txt("unitsPerPackageUom", "Units per Package UOM"),
  num("valueAvailable", "Value Available"),
  txt("vendorItemCode", "Vendor Item Code"),
  num("volumeAvailable", "Volume Available"),
  num("warrantyMeterHours", "Warranty Meter Hours"),
  num("warrantyMeterMiles", "Warranty Meter Miles"),
  num("warrantyMonths", "Warranty Months"),
  txt("warrantyFlag", "Warranty Flag"),
  txt("warrantyPeriodTypeFlag", "Warranty Period Type"),
  txt("wearHouseName", "Warehouse Name"),
  txt("weightBasisFlag", "Weight Basis Flag"),
  ...udf("userDefinedCode", "Code", "txt"),
  ...udf("userDefinedReference", "Reference", "txt"),
  ...udf("userDefinedDate", "Date", "date"),
  ...udf("userDefinedQuantity", "Quantity", "num"),
];

export default ITEM_MASTER_COLUMNS;
